import { useState, useEffect } from "react";
import useEmailSender from "../main/contact/useEmailSender";
import { trackEvent } from "../util/analytics";

/** 처음 들어온 링크의 UTM 및 유입 정보. 사이트 안에서 이동해도 남도록 세션에 보관 */
function consultSource() {
  try {
    const params = new URLSearchParams(window.location.search);
    const source = params.get("utm_source");
    const medium = params.get("utm_medium");
    const campaign = params.get("utm_campaign");
    if (!sessionStorage.getItem("sc-entry-path")) {
      sessionStorage.setItem("sc-entry-path", window.location.pathname + window.location.search);
    }
    if (!sessionStorage.getItem("sc-referrer") && document.referrer) {
      sessionStorage.setItem("sc-referrer", document.referrer);
    }
    const utmParts = [source, medium, campaign].filter(Boolean);
    if (utmParts.length > 0) {
      sessionStorage.setItem("sc-utm", utmParts.join(" / "));
    }
    const utm = sessionStorage.getItem("sc-utm") || "";
    const referrer = sessionStorage.getItem("sc-referrer") || "";
    const entryPath = sessionStorage.getItem("sc-entry-path") || "";
    const parts = [];
    if (utm) parts.push(`UTM: ${utm}`);
    if (referrer) parts.push(`Referrer: ${referrer}`);
    if (entryPath) parts.push(`진입 경로: ${entryPath}`);
    return parts.join("\n") || "직접 방문";
  } catch {
    return "직접 방문";
  }
}

const SERVICE_MAP = {
  subscription: "맞춤 시스템 구독",
  sero: "세로 ERP 맞춤형 상담",
  seroStd: "세로 ERP 표준형 신청",
  erp: "맞춤 ERP 도입",
  grant: "정부지원금 구축",
  operation: "기존 시스템 전담 운영",
};

/**
 * 상담 팝업 상태 관리 훅
 * @param {Object} options
 * @param {string} [options.defaultService="맞춤 시스템 구독"] - 기본 관심 서비스
 */
export default function useConsultPopup({ defaultService = "맞춤 시스템 구독" } = {}) {
  const [consultOpen, setConsultOpen] = useState(false);
  const [consult, setConsult] = useState({
    company: "",
    name: "",
    contact: "",
    service: defaultService,
    details: "",
    privacy: false,
  });
  const { sendEmail, isSending } = useEmailSender();

  // 버튼에서 관심 서비스를 지정해 열 수 있다. onClick 이벤트가 넘어오면 무시한다.
  const openConsult = (service) => {
    const nextService = typeof service === "string" ? service : consult.service;
    if (nextService !== consult.service) setConsult((current) => ({ ...current, service: nextService }));
    setConsultOpen(true);
    trackEvent('consult_open', {
      service: nextService,
      source_page: window.location.pathname,
    });
  };

  const closeConsult = () => {
    if (!isSending) setConsultOpen(false);
  };

  const updateConsult = (event) => {
    const { name, value, type, checked } = event.target;
    setConsult((current) => ({ ...current, [name]: type === "checkbox" ? checked : value }));
  };

  const submitConsult = async (event) => {
    event.preventDefault();
    if (!consult.privacy) return;

    // Get UTM source for analytics
    const utmSource = sessionStorage.getItem("sc-utm")?.split(" / ")[0] || "direct";

    const details = [
      `회사명: ${consult.company || "미입력"}`,
      `관심 서비스: ${consult.service}`,
      `유입 경로: ${consultSource() || "직접 방문"}`,
      "개인정보 수집·이용 동의: 동의",
      "",
      "현재 고민 또는 문의 내용:",
      consult.details || "상담 시 설명 예정",
    ].join("\n");
    const result = await sendEmail(consult.name, consult.contact, details);
    if (result?.success) {
      // Track successful lead generation (no PII in params)
      trackEvent('generate_lead', {
        service: consult.service,
        utm_source: utmSource,
      });
      setConsult({ company: "", name: "", contact: "", service: defaultService, details: "", privacy: false });
      setConsultOpen(false);
    } else {
      // Track submission error
      trackEvent('consult_submit_error', {
        service: consult.service,
        source_page: window.location.pathname,
      });
    }
  };

  // 딥링크 처리: ?consult=open&service=xxx
  useEffect(() => {
    consultSource(); // 유입 정보 저장
    const params = new URLSearchParams(window.location.search);
    if (params.get("consult") === "open") {
      setConsultOpen(true);
      const s = params.get("service");
      if (SERVICE_MAP[s]) {
        setConsult((current) => ({ ...current, service: SERVICE_MAP[s] }));
      }
      // 팝업이 열리면 consult, service 파라미터만 제거 (utm은 유지)
      const cleanParams = new URLSearchParams(window.location.search);
      cleanParams.delete("consult");
      cleanParams.delete("service");
      const newUrl = cleanParams.toString()
        ? `${window.location.pathname}?${cleanParams.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, "", newUrl);
    }
  }, []);

  // ESC 키로 팝업 닫기 + body overflow 제어
  useEffect(() => {
    if (!consultOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !isSending) setConsultOpen(false);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [consultOpen, isSending]);

  return {
    consultOpen,
    setConsultOpen,
    consult,
    setConsult,
    isSending,
    openConsult,
    closeConsult,
    updateConsult,
    submitConsult,
  };
}
